// src/pages/DanhGiaCuoiKy/DanhGiaCuoiKy.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "../../context/AuthContext";
import { getAllLopHoc, getAllNganh } from "../../services/nguoiDungService";
import { getHocSinhByLop } from "../../services/hocSinhService";
import { getDanhGiaByLop, saveDanhGia } from "../../services/DanhGiaService";
import { useDanhGia } from "../../hooks/useDanhGia";
import { useToast } from "../../components/Toast/ToastContext";
import { ROLES } from "../../constants";
import { getNamHocActive } from "../../services/namHocService";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import DanhGiaCuoiKyMobile from "./DanhGiaCuoiKyMobile";
import DanhGiaCuoiKyDesktop from "./DanhGiaCuoiKyDesktop";
import FormDanhGia from "./FormDanhGia";
import FormCaNam from "./FormCaNam";
import BottomSheet from "../../components/BottomSheet/BottomSheet";

const DanhGiaCuoiKy = () => {
  const { user, hasRole } = useAuthContext();
  const { success, error } = useToast();
  const isAdmin = hasRole(ROLES.ADMIN);
  const isTruongNganh = hasRole(ROLES.TRUONG_NGANH);
  const isGVCN = hasRole(ROLES.GVCN);
  const canEdit = isGVCN || isAdmin;
  const canXemTongHop = isAdmin || isTruongNganh;

  const [lopHocList, setLopHocList] = useState([]);
  const [nganhList, setNganhList] = useState([]);
  const [selectedLop, setSelectedLop] = useState(user?.maLopCN || "");
  const [selectedLopName, setSelectedLopName] = useState("");
  const [namHoc, setNamHoc] = useState("2025-2026");
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [currentHS, setCurrentHS] = useState(null);
  const [activeTab, setActiveTab] = useState('danhgia'); // 'danhgia' | 'tonghop'
  const [hocKyTab, setHocKyTab] = useState('HK1'); // 'HK1' | 'HK2' | 'caNam'

  // Dữ liệu cả năm
  const [danhSachCaNam, setDanhSachCaNam] = useState([]);
  const [loadingCaNam, setLoadingCaNam] = useState(false);
  const [hasHK2, setHasHK2] = useState(false);

  // Tổng hợp
  const [danhSachTongHop, setDanhSachTongHop] = useState([]);
  const [loadingTongHop, setLoadingTongHop] = useState(false);
  const [selectedNganhXet, setSelectedNganhXet] = useState(user?.maNganh || "");

  const { hocSinh, danhGiaMap, loading, loadData, calculateForHS, save } = useDanhGia(selectedLop, hocKyTab === 'caNam' ? 'HK2' : hocKyTab, namHoc, user.uid);

  useEffect(() => {
    const init = async () => {
      try { const nh = await getNamHocActive(); if (nh) setNamHoc(nh.tenNamHoc || nh.id || '2025-2026'); } catch(e) {}
    };
    init();
  }, []);

  useEffect(() => {
    getAllLopHoc().then(data => {
      setLopHocList(data);
      if (selectedLop) {
        const found = data.find(l => l.maLop === selectedLop);
        if (found) setSelectedLopName(found.tenLop);
      }
    });
    getAllNganh().then(setNganhList);
  }, [selectedLop]);

  useEffect(() => { 
    if (hocKyTab !== 'caNam') loadData(); 
  }, [loadData, hocKyTab]);

  // Kiểm tra có dữ liệu HK2 chưa
  useEffect(() => {
    if (!selectedLop) return;
    const check = async () => {
      try {
        const snap = await getDocs(query(collection(db, "danhGiaCuoiKy"), 
          where("lopId", "==", selectedLop), where("hocKy", "==", "HK2")));
        setHasHK2(!snap.empty);
      } catch(e) { setHasHK2(false); }
    };
    check();
  }, [selectedLop, danhGiaMap]);

  // Load dữ liệu cả năm
  const loadCaNam = useCallback(async () => {
    if (!selectedLop) return;
    setLoadingCaNam(true);
    try {
      const [hsList, dgHK1, dgHK2] = await Promise.all([
        getHocSinhByLop(selectedLop),
        getDanhGiaByLop(selectedLop, 'HK1'),
        getDanhGiaByLop(selectedLop, 'HK2'),
      ]);

      const data = hsList.map(hs => {
        const hk1 = dgHK1.find(d => d.hocSinhId === hs.id);
        const hk2 = dgHK2.find(d => d.hocSinhId === hs.id);
        
        const dtbHK1 = hk1?.diemTB || 0;
        const dtbHK2 = hk2?.diemTB || 0;
        const dtbCaNam = dtbHK1 > 0 && dtbHK2 > 0 ? parseFloat(((dtbHK1 + dtbHK2 * 2) / 3).toFixed(1)) : 0;
        
        const ccHK1 = hk1?.chuyenCan || 0;
        const ccHK2 = hk2?.chuyenCan || 0;
        const ccCaNam = Math.round((ccHK1 + ccHK2) / 2);

        return {
          hocSinhId: hs.id,
          hoTen: hs.hoTen || '',
          tenThanh: hs.tenThanh || '',
          dtbHK1, dtbHK2, dtbCaNam,
          ccHK1, ccHK2, ccCaNam,
          nhanBanHK1: hk1?.nhanBan || '',
          nhanBanHK2: hk2?.nhanBan || '',
          xepLoaiHK1: hk1?.xepLoai || '',
          xepLoaiHK2: hk2?.xepLoai || '',
          ketQua: hk2?.ketQua || hs.trangThai === 'luuBan' ? 'luuBan' : 'lenLop',
          daCoDuLieu: !!(hk1 && hk2 && hk1.trangThai === 'daHoanThanh' && hk2.trangThai === 'daHoanThanh'),
        };
      }).sort((a, b) => a.hoTen.localeCompare(b.hoTen));

      setDanhSachCaNam(data);
    } catch(e) { console.error(e); }
    finally { setLoadingCaNam(false); }
  }, [selectedLop]);

  useEffect(() => {
    if (hocKyTab === 'caNam') loadCaNam();
  }, [hocKyTab, loadCaNam]);

  // Load tổng hợp
  const loadTongHop = useCallback(async () => {
    setLoadingTongHop(true);
    try {
      const dgSnapshot = await getDocs(query(collection(db, "danhGiaCuoiKy"), where("namHoc", "==", namHoc)));
      const danhGiaList = dgSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const hsSnapshot = await getDocs(query(collection(db, "hocSinh"), where("trangThai", "in", ["dangHoc", "luuBan"])));
      const hsList = hsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const lopTrongNganh = selectedNganhXet ? lopHocList.filter(l => l.nganhId === selectedNganhXet).map(l => l.maLop) : [];

      const ds = hsList
        .filter(hs => isTruongNganh && selectedNganhXet ? lopTrongNganh.includes(hs.lopHienTaiId) : true)
        .map(hs => {
          const hk1 = danhGiaList.find(d => d.hocSinhId === hs.id && d.hocKy === 'HK1');
          const hk2 = danhGiaList.find(d => d.hocSinhId === hs.id && d.hocKy === 'HK2');
          return {
            hocSinhId: hs.id, hoTen: hs.hoTen || '', tenThanh: hs.tenThanh || '',
            lopHienTaiTen: lopHocList.find(l => l.maLop === hs.lopHienTaiId)?.tenLop || '',
            daDanhGiaHK1: !!hk1?.trangThai === 'daHoanThanh',
            daDanhGiaHK2: !!hk2?.trangThai === 'daHoanThanh',
            ketQua: hk2?.ketQua || (hs.trangThai === 'luuBan' ? 'luuBan' : 'lenLop'),
          };
        }).sort((a, b) => a.hoTen.localeCompare(b.hoTen));
      setDanhSachTongHop(ds);
    } catch(e) { console.error(e); }
    finally { setLoadingTongHop(false); }
  }, [namHoc, selectedNganhXet, lopHocList, isTruongNganh]);

  useEffect(() => {
    if (activeTab === 'tonghop' && lopHocList.length > 0) loadTongHop();
  }, [activeTab, loadTongHop, lopHocList]);

  const handleSaveComplete = async (hsId, data) => {
    const result = await save(hsId, data);
    if (result.success) {
      if (data.trangThai === 'daHoanThanh' && data.ketQua && hocKyTab === 'caNam') {
        try {
          const newTrangThai = data.ketQua === 'luuBan' ? 'luuBan' : 'dangHoc';
          await updateDoc(doc(db, "hocSinh", hsId), { trangThai: newTrangThai, ngayCapNhat: new Date().toISOString() });
        } catch(e) { console.error(e); }
      }
      success('Đã lưu!');
      setShowForm(false);
      if (hocKyTab === 'caNam') loadCaNam();
      else loadData();
    }
  };

  const soDone = hocSinh.filter(hs => danhGiaMap[hs.id]?.trangThai === 'daHoanThanh').length;

  return (
    <div className="space-y-4 pb-20 md:pb-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h2 className="text-[24px] md:text-[28px] font-bold text-primary">📋 Đánh giá & Xét lên lớp</h2><p className="text-caption text-on-surface-variant">{namHoc} · {selectedLopName || 'Chọn lớp'}</p></div>
      </div>

      {/* Tabs chính */}
      <div className="flex gap-1 bg-surface-container rounded-xl p-1">
        <button onClick={() => setActiveTab('danhgia')} className={`flex-1 py-2.5 rounded-lg text-body-md font-bold transition-colors ${activeTab === 'danhgia' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>📝 Đánh giá</button>
        {canXemTongHop && (
          <button onClick={() => setActiveTab('tonghop')} className={`flex-1 py-2.5 rounded-lg text-body-md font-bold transition-colors ${activeTab === 'tonghop' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>📊 Tổng hợp</button>
        )}
      </div>

      {activeTab === 'danhgia' && (
        <>
          {/* Tabs học kỳ */}
          <div className="flex gap-1 bg-surface-container rounded-xl p-1">
            <button onClick={() => setHocKyTab('HK1')} className={`flex-1 py-2 rounded-lg text-caption font-bold transition-colors ${hocKyTab === 'HK1' ? 'bg-secondary-container text-on-secondary-container' : 'text-on-surface-variant'}`}>📝 HK1</button>
            <button onClick={() => setHocKyTab('HK2')} className={`flex-1 py-2 rounded-lg text-caption font-bold transition-colors ${hocKyTab === 'HK2' ? 'bg-secondary-container text-on-secondary-container' : 'text-on-surface-variant'}`}>📝 HK2</button>
            {hasHK2 && (
              <button onClick={() => setHocKyTab('caNam')} className={`flex-1 py-2 rounded-lg text-caption font-bold transition-colors ${hocKyTab === 'caNam' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>📊 Cả năm</button>
            )}
          </div>

          {/* Form đánh giá HK1/HK2 */}
          {hocKyTab !== 'caNam' && (
            <>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <select value={selectedLop} onChange={e => setSelectedLop(e.target.value)} className="bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none">
                    <option value="">-- Chọn lớp --</option>{lopHocList.map(l => <option key={l.id} value={l.maLop}>{l.tenLop}</option>)}
                  </select>
                )}
                <span className="text-caption text-on-surface-variant">{selectedLopName} · {hocKyTab}</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-surface-container-lowest rounded-xl p-4 text-center border"><p className="text-[28px] font-bold text-primary">{hocSinh.length}</p><p className="text-caption">Tổng</p></div>
                <div className="bg-surface-container-lowest rounded-xl p-4 text-center border"><p className="text-[28px] font-bold text-green-600">{soDone}</p><p className="text-caption">Đã xong</p></div>
                <div className="bg-surface-container-lowest rounded-xl p-4 text-center border"><p className="text-[28px] font-bold text-yellow-600">{hocSinh.length - soDone}</p><p className="text-caption">Chưa xong</p></div>
              </div>

              <div className="flex gap-2">
                {[{ value: 'all', label: 'Tất cả' }, { value: 'pending', label: 'Chưa ĐG' }, { value: 'done', label: 'Đã xong' }].map(f => (
                  <button key={f.value} onClick={() => setFilter(f.value)} className={`px-4 py-2 rounded-full text-caption font-bold ${filter === f.value ? 'bg-secondary-container text-on-secondary-container' : 'bg-surface-container text-on-surface-variant'}`}>{f.label}</button>
                ))}
              </div>

              {loading ? <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>
              : !selectedLop ? <div className="text-center py-12 text-on-surface-variant"><span className="material-symbols-outlined text-[48px]">inbox</span><p>Chọn lớp để xem</p></div>
              : (
                <>
                  <div className="md:hidden"><DanhGiaCuoiKyMobile filteredHS={hocSinh.filter(hs => { const dg = danhGiaMap[hs.id]; if (filter === 'done') return dg?.trangThai === 'daHoanThanh'; if (filter === 'pending') return !dg || dg.trangThai !== 'daHoanThanh'; return true; })} danhGiaMap={danhGiaMap} onSelect={(hs) => { setCurrentHS(hs); setShowForm(true); }} /></div>
                  <div className="hidden md:block"><DanhGiaCuoiKyDesktop filteredHS={hocSinh.filter(hs => { const dg = danhGiaMap[hs.id]; if (filter === 'done') return dg?.trangThai === 'daHoanThanh'; if (filter === 'pending') return !dg || dg.trangThai !== 'daHoanThanh'; return true; })} danhGiaMap={danhGiaMap} onSelect={(hs) => { setCurrentHS(hs); setShowForm(true); }} /></div>
                </>
              )}
            </>
          )}

          {/* Form Cả năm */}
          {hocKyTab === 'caNam' && (
            <>
              {loadingCaNam ? <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>
              : danhSachCaNam.length === 0 ? <div className="text-center py-12 text-on-surface-variant"><span className="material-symbols-outlined text-[48px]">inbox</span><p>Chưa có dữ liệu</p></div>
              : (
                <div className="bg-surface-container-lowest rounded-xl border border-outline-variant overflow-hidden">
                  <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                    <table className="w-full text-left text-body-md">
                      <thead className="sticky top-0 bg-surface-container-low z-10">
                        <tr className="border-b">
                          <th className="px-3 py-3 text-label-caps">HS</th>
                          <th className="px-3 py-3 text-label-caps">ĐTB HK1</th>
                          <th className="px-3 py-3 text-label-caps">ĐTB HK2</th>
                          <th className="px-3 py-3 text-label-caps">ĐTB CN</th>
                          <th className="px-3 py-3 text-label-caps">CC</th>
                          <th className="px-3 py-3 text-label-caps">Kết quả</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {danhSachCaNam.map(item => (
                          <tr key={item.hocSinhId} className="hover:bg-surface-container-low cursor-pointer" onClick={() => { setCurrentHS(item); setShowForm(true); }}>
                            <td className="px-3 py-2 font-semibold">{item.tenThanh ? `${item.tenThanh} ` : ''}{item.hoTen}</td>
                            <td className="px-3 py-2">{item.dtbHK1 || '—'}</td>
                            <td className="px-3 py-2">{item.dtbHK2 || '—'}</td>
                            <td className="px-3 py-2 font-bold">{item.dtbCaNam || '—'}</td>
                            <td className="px-3 py-2">{item.ccCaNam}%</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-label-caps font-bold ${!item.daCoDuLieu ? 'bg-gray-100 text-gray-600' : item.ketQua === 'lenLop' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {!item.daCoDuLieu ? 'Chưa ĐG' : item.ketQua === 'lenLop' ? 'Lên lớp' : 'Lưu ban'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* BottomSheet Form */}
          <BottomSheet isOpen={showForm} onClose={() => setShowForm(false)} title={currentHS ? `✏️ ${currentHS.tenThanh || ''} ${currentHS.hoTen}` : ''}>
            {currentHS && hocKyTab !== 'caNam' && (
              <FormDanhGia hs={currentHS} lopId={selectedLop} hocKy={hocKyTab} existingData={danhGiaMap[currentHS.id]}
                calculateForHS={calculateForHS} onSave={handleSaveComplete}
                onClose={() => { setShowForm(false); loadData(); }} canEdit={canEdit} />
            )}
            {currentHS && hocKyTab === 'caNam' && (
              <FormCaNam data={currentHS} lopId={selectedLop} namHoc={namHoc}
                onSave={handleSaveComplete} onClose={() => { setShowForm(false); loadCaNam(); }} canEdit={canEdit} />
            )}
          </BottomSheet>
        </>
      )}

      {/* Tab Tổng hợp */}
      {activeTab === 'tonghop' && canXemTongHop && (
        <>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <select value={selectedNganhXet} onChange={e => setSelectedNganhXet(e.target.value)} className="bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none">
                <option value="">Tất cả ngành</option>{nganhList.map(n => <option key={n.id} value={n.maNganh}>{n.tenNganh}</option>)}
              </select>
            )}
            <button onClick={loadTongHop} className="px-4 py-2 bg-surface border border-outline-variant rounded-xl text-body-md"><span className="material-symbols-outlined text-[18px]">refresh</span></button>
          </div>

          {loadingTongHop ? <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>
          : danhSachTongHop.length === 0 ? <div className="text-center py-12 text-on-surface-variant"><span className="material-symbols-outlined text-[48px]">inbox</span><p>Chưa có dữ liệu</p></div>
          : (
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant overflow-hidden">
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="w-full text-left text-body-md">
                  <thead className="sticky top-0 bg-surface-container-low z-10">
                    <tr className="border-b">
                      <th className="px-3 py-3 text-label-caps">Học sinh</th>
                      <th className="px-3 py-3 text-label-caps">Lớp</th>
                      <th className="px-3 py-3 text-label-caps">HK1</th>
                      <th className="px-3 py-3 text-label-caps">HK2</th>
                      <th className="px-3 py-3 text-label-caps">Kết quả</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {danhSachTongHop.map(item => (
                      <tr key={item.hocSinhId} className="hover:bg-surface-container-low">
                        <td className="px-3 py-2 font-semibold">{item.tenThanh ? `${item.tenThanh} ` : ''}{item.hoTen}</td>
                        <td className="px-3 py-2 text-caption">{item.lopHienTaiTen}</td>
                        <td className="px-3 py-2">{item.daDanhGiaHK1 ? '✅' : '❌'}</td>
                        <td className="px-3 py-2">{item.daDanhGiaHK2 ? '✅' : '❌'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-label-caps font-bold ${item.ketQua === 'lenLop' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {item.ketQua === 'lenLop' ? 'Lên lớp' : 'Lưu ban'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DanhGiaCuoiKy;